import IdListDTO from 'models/dto/IdListDTO';
import DashboardDTO from 'models/dto/dashboardDTO';

//
// Project v2 project aggregate - the wire shapes of ProjectController and the tier read.
//
// Canonical source: isoplanner-api REDLAB.ISOPlanner.Core/Data/DTOs/Project/*.cs. Property names
// are the camelCased C# properties, which is what the API's serializer puts on the wire.
//
// Two wire facts shape the optionality below:
// 1. httpService's `nullsToUndefined` interceptor turns every C# `null` into `undefined`, so a C#
//    `bool?` is `boolean | undefined` here, never `boolean | null`.
// 2. GET projects returns `ProjectSummaryDTO[]`, without milestones, rooms, tier, track or
//    dashboard. The mutation responses (create, PUT projects/{id}, the state and edit routes) are a
//    `ProjectDTO` with only the summary filled; only GET projects/{id} fills the rest - see
//    `hasDetail` in models/projects/project.ts.
// 3. A member the API has no value for is left out of the JSON (WhenWritingNull) instead of a 0 or "".
//
// A room's confirmation is `ProjectRoomAvailabilityDTO.confirmed`: true exactly when the room's
// anchor task is completed, which only happens when an apply settled with zero failed items.
//

/**
 * One step of one milestone: the platform task a person completes, its authored words, and its
 * place within the milestone.
 */
export class ProjectStepDTO {
  taskId: number;

  /** The task's own name, authored in the package. */
  name: string;

  /** The task's place within its milestone, ascending. */
  sortOrder: number;

  completed: boolean;

  /** ISO 8601. Undefined while the task is not completed. */
  completedDate?: string;

  /** The seed's step key; undefined for a step authored by hand. */
  key?: string;

  /** True while the step is not completed and work on it has begun (a checklist item ticked, or its task state). */
  started?: boolean;

  constructor() {
    this.taskId = 0;
    this.name = '';
    this.sortOrder = 0;
    this.completed = false;
  }
}

/**
 * One project milestone as the project read shows it. Milestones arrive in the order the package
 * authored them; there is no fixed count and no fixed set of names.
 */
export class ProjectMilestoneDTO {
  /** The milestone's identity: its own platform task. */
  taskId: number;

  /** The milestone task's own name - what every screen labels it with. */
  name: string;

  /** The milestone task's own description. */
  description: string;

  completed: boolean;

  /** ISO 8601. Undefined while the milestone task is not completed. */
  completedDate?: string;

  /** A projectMilestoneState value: todo | inProgress | done. Not the same fact as `completed`. */
  state: string;

  /** ISO 8601. The milestone task's start date. */
  startDate?: string;

  /** ISO 8601. The milestone task's deadline; undefined when it carries none. */
  deadline?: string;

  tasksDone: number;

  /** Zero is a fact: this milestone carries no steps. */
  tasksTotal: number;

  /** Confirmed rooms anchored under this milestone. */
  roomsDone: number;

  /** Rooms anchored under this milestone. Zero is a fact: it anchors none. */
  roomsTotal: number;

  /**
   * Single read only. Empty on the create and list reads means not loaded rather than "this
   * milestone has no steps"; `tasksTotal` is the field for that fact.
   */
  tasks: ProjectStepDTO[];

  /** Single read only, `projectMilestoneSwitchBlocker`: why the caller cannot switch it now; absent when they can. */
  switchBlocker?: string | null;

  /** True when the milestone closes by itself once every room and step under it is done; false when a person switches it. */
  closesWhenStepsDone: boolean;

  constructor() {
    this.taskId = 0;
    this.name = '';
    this.description = '';
    this.completed = false;
    this.state = '';
    this.tasksDone = 0;
    this.tasksTotal = 0;
    this.roomsDone = 0;
    this.roomsTotal = 0;
    this.tasks = [];
    this.closesWhenStepsDone = false;
  }
}

/**
 * One room's availability on the project read. Every registered room appears once, in room order; a
 * room this project does not anchor returns notSeeded rather than being omitted. A short list means
 * the read did not load it.
 */
export class ProjectRoomAvailabilityDTO {
  /** The room's key - its identity, and the route segment. */
  roomKey: string;

  /** The EntityTypes value this room covers. An attribute, not its identity. */
  kind: number;

  /** A projectRoomAvailability value: live | notSeeded. */
  availability: string;

  /**
   * The decision routes this room dispatches, ordinal-sorted. Empty for a room that dispatches
   * none.
   */
  actions: string[];

  /** True when a decision about one of this room's items may carry an editedDescription. */
  supportsEditDescription: boolean;

  /** This room's anchor task; undefined when the project anchors none. */
  deliverableTaskId?: number;

  /** The anchor task's authored name; renderer identity is roomKey. */
  name?: string;

  /**
   * The milestone task this room's anchor hangs under; undefined when this project anchors no
   * such room.
   */
  milestoneTaskId?: number;

  /** This room's position within its milestone, zero-based, in room order. */
  sortOrder: number;

  confirmed: boolean;

  /**
   * When the anchor task was ticked, as an ISO date-time. Undefined whenever `confirmed` is false,
   * and on a confirmed row the server could not date - a room is confirmed by the flag, never by
   * the presence of this.
   */
  confirmedDate?: string;

  /**
   * The anchor task's deadline, as an ISO date-time. Meaningful only once the project's track
   * carries a start date; before that it is the install plan's date.
   */
  deadline?: string;

  /**
   * False for a room that is done when its data says so: it is never confirmed by hand, and the
   * apply refuses a confirm with PROJECT0033.
   */
  confirmable: boolean;

  /**
   * How far a live, confirmable room that is not confirmed is from its own data; undefined for every other room and for
   * a room that does not measure it.
   */
  progress?: ProjectRoomProgressDTO;

  /**
   * A live, confirmable room that is not confirmed, whose own measure is complete or that measures none: only the
   * confirm is left.
   */
  readyToConfirm: boolean;

  /**
   * The room is done, as the server decides it from stored facts: a confirmable room once confirmed, an inquiry send
   * room once every role it asks has its sent inquiry, the inquiry progress room once every sent inquiry is completed.
   * The milestone's `roomsDone` counts it.
   */
  done: boolean;

  /** Work in the room is recorded: done, progress above 0, or an inquiry of the room sent. */
  started: boolean;

  constructor() {
    this.roomKey = '';
    this.kind = 0;
    this.availability = '';
    this.actions = [];
    this.supportsEditDescription = false;
    this.sortOrder = 0;
    this.confirmed = false;
    this.confirmable = true;
    this.readyToConfirm = false;
    this.done = false;
    this.started = false;
  }
}

/** A room's progress from its own data: `done` of `total`. */
export class ProjectRoomProgressDTO {
  done: number;

  total: number;

  constructor() {
    this.done = 0;
    this.total = 0;
  }
}

/**
 * One band of the timeline: a stretch of stage work, the dates it ran between, and the share of the
 * project it carries. The bands are contiguous and in project order, so a rail draws them end to
 * end without arranging anything itself.
 */
export class ProjectTrackSegmentDTO {
  /** The milestone task this band is over. Undefined on a current band that names no milestone. */
  milestoneTaskId?: number;

  /** A projectTrackSegmentKind value: actual | current | projected | todo. */
  kind: string;

  /** A projectMilestoneState value: the milestone's state at the moment this was computed. */
  state: string;

  /** ISO 8601. Undefined when the band has no start to draw from. */
  from?: string;

  /** ISO 8601. The completion date, today, or the projection - whichever this band's kind means. */
  to?: string;

  /** The milestone's share of the project, 0..1. Zero on the current band, which is drawn twice. */
  weight: number;

  constructor() {
    this.kind = '';
    this.state = '';
    this.weight = 0;
  }
}

/** The track's one message: a code to render and a tone to render it in. */
export class ProjectTrackMessageDTO {
  /** A projectTrackMessageTone value: amber | red | info | quiet. */
  tone: string;

  /** A projectTrackMessageCode value: which sentence. */
  code: string;

  constructor() {
    this.tone = '';
    this.code = '';
  }
}

/** One suggested next action, and its impact in days. */
export class ProjectTrackMoveDTO {
  /** A projectTrackMoveKind value. */
  kind: string;

  /** The milestone task this move is about. Undefined on a move about the target date. */
  milestoneTaskId?: number;

  /** The task to complete, when the move names one. */
  taskId?: number;

  /** The key of the room to open, when the move names one. */
  roomKey?: string;

  /** Whole days this move pulls the forecast in by. 0 is a real value; undefined moves nothing. */
  impactDays?: number;

  /** True on the one move a rail puts forward. At most one move carries it. */
  primary: boolean;

  /** What is still open in the room an openRoom move names: at most three facts, most important first. */
  todo?: ProjectRoomTodoDTO[];

  constructor() {
    this.kind = '';
    this.primary = false;
  }
}

/** One thing still open in a room: a projectRoomTodoKey and how many (always above 0). */
export class ProjectRoomTodoDTO {
  key: string;

  count: number;

  constructor() {
    this.key = '';
    this.count = 0;
  }
}

/**
 * The forecasted completion date, compared against the owner's target, and the suggested next
 * moves.
 *
 * Every date here is computed server-side by the API's forecast engine against `today`, so the same
 * project reports the same track on any machine; the web positions these dates and never derives
 * one.
 */
export class ProjectTrackDTO {
  /**
   * A projectTrackState value: onTrack | atRisk | stalled | behind | notStarted | completed |
   * archived.
   */
  state: string;

  doneCount: number;

  /** Total milestone count in the journey, as authored by the package. */
  totalCount: number;

  /** ISO 8601. The date the forecast measures from. Undefined until the project has started. */
  startDate?: string;

  /** ISO 8601. The instant this track was computed for. Every other date here is relative to it. */
  today: string;

  /** ISO 8601 date. Undefined when no target was named. */
  targetDate?: string;

  /** A projectTrackForecastKind value: forecast | typical | none. */
  forecastKind: string;

  /**
   * ISO 8601. The first day of the month containing the forecast date. Undefined when there is
   * none.
   */
  forecastMonth?: string;

  /** ISO 8601. The P50 completion day. Undefined when there is none. */
  forecastDate?: string;

  /** Whole days the forecast is past what it is compared against; negative is early. */
  slipDays?: number;

  /**
   * A projectTargetFit value: comfortable | onPlan | ambitious | veryAmbitious | none. How the
   * target date compares with the forecast.
   */
  targetFit: string;

  /** Whole days since the last thing that happened on this project. */
  idleDays: number;

  segments: ProjectTrackSegmentDTO[];

  /** Undefined when the track has nothing to say. */
  message?: ProjectTrackMessageDTO;

  /** Empty for a completed or archived project, which has nothing left to do. */
  moves: ProjectTrackMoveDTO[];

  /**
   * SHA-256 of the facts this track came from, so a caller can tell a moved track from a re-read
   * one.
   */
  stateHash: string;

  constructor() {
    this.state = '';
    this.doneCount = 0;
    this.totalCount = 0;
    this.today = '';
    this.forecastKind = '';
    this.targetFit = '';
    this.idleDays = 0;
    this.segments = [];
    this.moves = [];
    this.stateHash = '';
  }
}

/**
 * One project as the list read (GET projects) returns it: no milestones, rooms, tier, track or
 * dashboard. The full read is `ProjectDTO`, which extends this.
 */
export class ProjectSummaryDTO {
  projectId: number;

  /** The organization-default translation; undefined when the project carries none. */
  name?: string;

  /** The project's own description (rich text); undefined when it has none. */
  description?: string;

  /** ProjectState: active 0 | completed 1 | archived 9. */
  state: number;

  /** True when every milestone is done. Derived server-side. */
  isCompleted: boolean;

  /**
   * The package this project came from. Undefined on an authoring project and on a project created
   * before packages were required.
   */
  packageId?: number;

  /** False when an owned package carries this project; the API refuses the delete (PROJECT0009). */
  canDelete: boolean;

  /**
   * The authorization schema this project is narrowed by; undefined when every member of the
   * organization can see it. Read-only: it is assigned through PUT auth/schemas/{id}/apply, the
   * same route every other authorized entity uses.
   */
  authSchemaId?: number;

  /** ISO 8601 date. The target date the project aims at; undefined when none was named. */
  targetDate?: string;

  normIds: number[];

  /** The tags on this project - display and grouping only, never a gate. */
  tagIds?: IdListDTO;

  /** ISO 8601. */
  created: string;

  constructor() {
    this.projectId = 0;
    this.state = 0;
    this.isCompleted = false;
    this.canDelete = true;
    this.normIds = [];
    this.created = '';
  }
}

/**
 * The project, as the single read (GET projects/{id}) returns it. The mutation responses are the
 * same type with only the summary filled: `milestones` and `rooms` empty, no tier, track or
 * dashboard.
 */
export class ProjectDTO extends ProjectSummaryDTO {
  /** Single read only. */
  tier?: ProjectTierDTO;

  /** Single read only. Empty on a mutation response means not loaded. */
  milestones: ProjectMilestoneDTO[];

  /** Single read only, then one row per registered room. Empty elsewhere means not loaded. */
  rooms: ProjectRoomAvailabilityDTO[];

  /** ISO 8601. Single read only: the day the work starts, the named start date or the creation date. */
  startDate?: string;

  /** Single read only. Undefined elsewhere means not loaded, never "this project has no track". */
  track?: ProjectTrackDTO;

  /** Overview widget host. Single read only; undefined until created. */
  dashboard?: DashboardDTO;

  /** Single read only: what the external audit step offers in this environment. */
  externalAudit?: ProjectExternalAuditDTO;

  /** Single read only, `projectWriteBlocker` but confirmed: why the caller cannot write the project now; absent when they can. */
  writeBlocker?: string | null;

  constructor() {
    super();
    this.milestones = [];
    this.rooms = [];
  }
}

/** The external audit options of the environment, the same for every project. */
export class ProjectExternalAuditDTO {
  /** False refuses the quote request (PROJECT0065); the client hides the button. */
  quoteRequestEnabled: boolean;

  /** The meeting link for a talk before the quote; undefined when none is configured. */
  meetingUrl?: string;

  constructor() {
    this.quoteRequestEnabled = false;
  }
}

/**
 * The lifecycle move and the target date, in one PUT.
 *
 * `expectedState` is the caller's optimistic-concurrency guard over the value it last read;
 * omitting it makes the write last-writer-wins, and a mismatch is refused with PROJECT0001.
 *
 * `state` is optional so a body that only moves the target date does not reactivate a completed
 * project: an omitted `state` leaves the project's state unchanged, and the API refuses a body that
 * omits both `state` and `targetDate`. `clearTargetDate` is a separate flag because an absent
 * `targetDate` already means "not provided".
 */
export class ProjectStateRequestDTO {
  /** ProjectState: active 0 | completed 1 | archived 9. Undefined leaves the state alone. */
  state?: number;

  /** The state the caller believes the project is in; undefined skips the guard. */
  expectedState?: number;

  /** ISO 8601 date, time zeroed. Refused when it is in the past at server date granularity. */
  targetDate?: string;

  /** True clears the target date. */
  clearTargetDate: boolean;

  constructor() {
    this.clearTargetDate = false;
  }
}

/**
 * The hypothetical target a forecast preview is measured against. POST projects/{id}/track does not
 * write. `clearTargetDate` is a separate flag because an absent `targetDate` already means "not
 * provided".
 */
export class ProjectTrackPreviewRequestDTO {
  /** ISO 8601 date, time zeroed. Refused when it is in the past at server date granularity. */
  targetDate?: string;

  /** True previews with no target. */
  clearTargetDate: boolean;

  constructor() {
    this.clearTargetDate = false;
  }
}

/**
 * What this organization's Project tier is, and what follows from it. A tier hides no record;
 * an expired trial only blocks writes.
 */
export class ProjectTierDTO {
  /** A projectTier value: trial | purchased | purchasedWithSharePoint. */
  tier: string;

  /** False on an expired trial alone; every purchased tier writes. */
  writesOpen: boolean;

  trialExpired: boolean;

  // The package facts below are populated only when the tier is queried for a specific project
  // (`?projectId=`): a tenant-wide entrance has no project, so it has no package, and every field
  // stays undefined. That means "this question was not asked", never "this organization owns
  // nothing".

  /**
   * The project's package. Undefined on the tenant-wide entrance, and on a package-less project.
   */
  packageId?: number;

  packageName?: string;

  /** PackageTenantStates: 3 activated, 99 installing, 100 content upload. */
  packageState?: number;

  /** Whole days left in the trial window. Undefined when this organization is not on a trial. */
  trialDaysLeft?: number;

  /**
   * The master package a preview package upgrades to, resolved server-side over `PackagePackage`.
   * The three fields are present together or not at all.
   */
  upgradePackageId?: number;

  upgradePackageName?: string;

  upgradePrice?: number;

  /**
   * The newest Live version of this project's package (same package code, higher version).
   * Undefined when there is none.
   */
  updatePackageId?: number;

  updatePackageName?: string;

  /** The newer version's version string, for example "1.1". */
  updatePackageVersion?: string;

  /**
   * PackageTenantStates of this organization on the newer version. Undefined when it holds no row:
   * the version has to be assigned or ordered before it can be installed.
   */
  updatePackageState?: number;

  constructor() {
    this.tier = '';
    this.writesOpen = false;
    this.trialExpired = false;
  }
}

/** The Kickstart offer in the organization's Store (GET projects/kickstart/offer). */
export class KickstartOfferDTO {
  /** The newest Kickstart version in the Store that is not a preview. */
  packageId?: number;

  /** The newest Kickstart preview (the trial twin) in the Store. */
  trialPackageId?: number;
}
